/**
 * Admin-tunable readability prefs (brightness, contrast, text scale, etc.).
 * Persisted in localStorage; applied as CSS variables on ``document.documentElement``.
 */

export const READABILITY_STORAGE_KEY = 'wiw_readability_prefs_v1';

/** @typedef {{
 *   brightness: number,
 *   contrast: number,
 *   saturate: number,
 *   textScale: number,
 *   letterSpacing: number,
 *   fontSmoothing: 'auto' | 'antialiased',
 *   reduceMotion: boolean,
 * }} ReadabilityPrefs */

/** @returns {ReadabilityPrefs} */
export function defaultReadabilityPrefs() {
  return {
    brightness: 1,
    contrast: 1,
    saturate: 1,
    textScale: 1,
    letterSpacing: 0,
    fontSmoothing: 'auto',
    reduceMotion: false,
  };
}

/**
 * Clamp + coerce a partial prefs object.
 *
 * @param {Partial<ReadabilityPrefs>|null|undefined} raw
 * @returns {ReadabilityPrefs}
 */
export function normalizeReadabilityPrefs(raw) {
  const base = defaultReadabilityPrefs();
  if (!raw || typeof raw !== 'object') return base;

  const num = (v, min, max, fallback) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  };

  const smoothing =
    raw.fontSmoothing === 'antialiased' || raw.fontSmoothing === 'auto'
      ? raw.fontSmoothing
      : base.fontSmoothing;

  return {
    brightness: num(raw.brightness, 0.7, 1.45, base.brightness),
    contrast: num(raw.contrast, 0.75, 1.5, base.contrast),
    saturate: num(raw.saturate, 0.6, 1.4, base.saturate),
    textScale: num(raw.textScale, 0.85, 1.35, base.textScale),
    letterSpacing: num(raw.letterSpacing, -0.02, 0.08, base.letterSpacing),
    fontSmoothing: smoothing,
    reduceMotion: Boolean(raw.reduceMotion),
  };
}

/**
 * @returns {ReadabilityPrefs}
 */
export function loadReadabilityPrefs() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return defaultReadabilityPrefs();
  }
  try {
    const raw = window.localStorage.getItem(READABILITY_STORAGE_KEY);
    if (!raw) return defaultReadabilityPrefs();
    return normalizeReadabilityPrefs(JSON.parse(raw));
  } catch {
    return defaultReadabilityPrefs();
  }
}

/**
 * @param {Partial<ReadabilityPrefs>} prefs
 * @returns {ReadabilityPrefs}
 */
export function saveReadabilityPrefs(prefs) {
  const next = normalizeReadabilityPrefs(prefs);
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      window.localStorage.setItem(READABILITY_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* private mode / quota */
    }
  }
  return next;
}

/**
 * Write CSS custom properties + data attrs used by ``index.css``.
 *
 * @param {Partial<ReadabilityPrefs>|null|undefined} prefs
 * @param {HTMLElement|null|undefined} [root]
 * @returns {ReadabilityPrefs}
 */
export function applyReadabilityPrefs(prefs, root) {
  const next = normalizeReadabilityPrefs(prefs);
  const el =
    root ||
    (typeof document !== 'undefined' ? document.documentElement : null);
  if (!el) return next;

  el.style.setProperty('--wiw-brightness', String(next.brightness));
  el.style.setProperty('--wiw-contrast', String(next.contrast));
  el.style.setProperty('--wiw-saturate', String(next.saturate));
  el.style.setProperty('--wiw-text-scale', String(next.textScale));
  el.style.setProperty('--wiw-letter-spacing', `${next.letterSpacing}em`);
  el.setAttribute('data-wiw-font-smoothing', next.fontSmoothing);
  el.setAttribute('data-wiw-reduce-motion', next.reduceMotion ? 'true' : 'false');
  el.setAttribute('data-wiw-readability', 'on');

  return next;
}

/**
 * Load from storage and apply to the document.
 *
 * @returns {ReadabilityPrefs}
 */
export function initReadabilityPrefs() {
  const prefs = loadReadabilityPrefs();
  return applyReadabilityPrefs(prefs);
}

/**
 * Reset to defaults, persist, and apply.
 *
 * @returns {ReadabilityPrefs}
 */
export function resetReadabilityPrefs() {
  const prefs = defaultReadabilityPrefs();
  saveReadabilityPrefs(prefs);
  return applyReadabilityPrefs(prefs);
}

/**
 * Persist + apply in one step (admin slider updates).
 *
 * @param {Partial<ReadabilityPrefs>} prefs
 * @returns {ReadabilityPrefs}
 */
export function commitReadabilityPrefs(prefs) {
  const next = saveReadabilityPrefs(prefs);
  return applyReadabilityPrefs(next);
}
